import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { PlusCircle } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import { useSnapshotScope } from '../../contexts/SnapshotScope';
import { useSimpleSnapshot } from '../../hooks/useSimpleSnapshot';
import { Asset } from '../../types';
import { PlatformKey } from '../../utils/platformConfig';
import { Carousel3D } from './Carousel3D';
import AssetCard from './AssetCard'; // NOTE: default export
import { EmbedWithPlaceholder } from '../../components/shared/ui/EmbedWithPlaceholder';
import { AssetsSkeleton } from '../../components/shared/ui/LazyFallbacks';
import { normalizeUrl } from '../../utils/embedDetect';
import { PLATFORM_CONFIGS } from '../../utils/platformConfig';
import { todayYMD, parseLocalDate } from '../../utils/datePlain';
import { validateEmbedInput, validateAssetTitle, validateDate } from '../../utils/inputValidation';
import { ProgressiveAssetLoader } from '../../components/shared/ui/ProgressiveAssetLoader';
import { smartScriptLoader } from '../../utils/smartScriptLoader';
import { logger } from '../../utils/logger';
import { showErrorToast, showWarningToast } from '../../hooks/useToast';
import { getCurrentWeekKey, getWeekStartDateFromKey } from '../../utils/weekKeyUtils';
import { ALLOW_PAST_WEEK_EDITS } from '../../config/auth';

import Modal from '../../components/shared/ui/Modal';
import SectionContainer from '../../components/shared/ui/SectionContainer';
import SectionHeader from '../../components/shared/ui/SectionHeader';

// ===== Layout mode (Carousel vs Masonry) =====
const LAYOUT_STORAGE_KEY = 'live-assets-layout-mode';

type LayoutMode = 'carousel' | 'masonry';

// Platform detection handlers
const PLATFORM_EMBED_HANDLERS = {
  'youtube-shorts': { patterns: ['youtube.com/shorts/'], platform: 'youtube-shorts' as PlatformKey, title: 'YouTube Shorts' },
  youtube: { patterns: ['youtube.com/watch', 'youtu.be/', 'youtube.com/embed/', 'youtube.com/playlist'], platform: 'youtube' as PlatformKey, title: 'YouTube Video' },
  instagram: { patterns: ['instagram.com/p/','instagram.com/reel/','data-instgrm-permalink','instagram-media','instagram.com/stories/'], platform: 'instagram' as PlatformKey, title: 'Instagram Post' },
  tiktok: { patterns: ['tiktok.com/@','tiktok.com/video/','tiktok-embed','data-video-id','tiktok.com/music/'], platform: 'tiktok' as PlatformKey, title: 'TikTok Video' },
  twitter: { patterns: ['twitter.com/','x.com/','data-url','twitter-tweet','twitter.com/status/','x.com/status/'], platform: 'twitter' as PlatformKey, title: 'Twitter Post' },
  facebook: { patterns: ['facebook.com/','fb.com/','data-href','fb-post','fb-video','facebook.com/plugins/','facebook.com/reel/'], platform: 'facebook' as PlatformKey, title: 'Facebook Post' },
  linkedin: { patterns: ['linkedin.com/','linkedin.com/embed/','linkedin.com/posts/','linkedin.com/feed/update/'], platform: 'linkedin' as PlatformKey, title: 'LinkedIn Post' }
};

interface LiveAssetsProps {
  bootData?: Asset[]; // Optional boot data for instant render
}

const LiveAssets: React.FC<LiveAssetsProps> = ({ bootData: initialBootData }) => {
  const { canEditSection, isActuallyAdmin } = useUser();
  const { workspaceId, periodKey } = useSnapshotScope();
  const isAdmin = isActuallyAdmin; // Check actual role, not effective role (viewer mode)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newAssetInput, setNewAssetInput] = useState('');
  const [newAssetTitle, setNewAssetTitle] = useState('');
  const [newAssetDate, setNewAssetDate] = useState('');

  // NEW: Layout mode with localStorage persistence
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(() => {
    try {
      const saved = localStorage.getItem(LAYOUT_STORAGE_KEY);
      return (saved === 'masonry' || saved === 'carousel') ? (saved as LayoutMode) : 'carousel';
    } catch {
      return 'carousel';
    }
  });

  // Performance optimization: Preload critical scripts
  useEffect(() => {
    smartScriptLoader.preloadCriticalScripts();
  }, []);

  const handleLayoutChange = (mode: LayoutMode) => {
    setLayoutMode(mode);
    try {
      localStorage.setItem(LAYOUT_STORAGE_KEY, mode);
    } catch (error) {
      // localStorage may be unavailable (private browsing, quota exceeded)
      if (import.meta.env.DEV) {
        logger.warn('Failed to save layout preference', { error }, 'LiveAssets');
      }
    }
  };

  // ✅ CRITICAL FIX: Always enable hook for save functionality
  // Boot data is just for initial render, but hook must be enabled for saves to work
  const { data: fetchedAssets, save, rootKey: sectionRootKey } = useSimpleSnapshot<Asset[]>({
    workspaceId,
    section: 'liveAssets',
    periodKey,
    defaultValue: [],
    enabled: true // ✅ Always enabled - boot data is just for initial render
  });
  
  // Track if we've already synced boot data to prevent restoring deleted items
  const hasSyncedBootDataRef = useRef(false);
  // Track if hook data has loaded (even if empty) to distinguish from "not loaded yet"
  const hasLoadedRef = useRef(false);
  
  // Track when fetched data loads (even if empty)
  useEffect(() => {
    if (fetchedAssets !== undefined) {
      hasLoadedRef.current = true;
    }
  }, [fetchedAssets]);
  
  // Sync boot data to hook state when provided (for instant render)
  // CRITICAL: Only sync once on initial mount when boot data exists and fetched data hasn't loaded yet
  // This prevents overwriting existing user data or restoring deleted items
  useEffect(() => {
    if (initialBootData && Array.isArray(initialBootData) && initialBootData.length > 0) {
      // Only sync if we haven't synced yet AND hook data hasn't loaded yet
      // This prevents restoring deleted items when array becomes empty
      if (!hasSyncedBootDataRef.current && !hasLoadedRef.current) {
        hasSyncedBootDataRef.current = true;
        save(initialBootData).catch((error) => {
          if (import.meta.env.DEV) {
            logger.error('Failed to sync boot data', { error }, 'LiveAssets');
          }
        });
      }
    }
  }, [initialBootData, save]);
  
  // Use boot data if available (instant), otherwise use fetched data
  // CRITICAL: Always use fetchedAssets for saves to ensure data persistence
  // Type guard: ensure assets is always an array
  const assets: Asset[] = (() => {
    const data = (initialBootData && initialBootData.length > 0 && (!fetchedAssets || fetchedAssets.length === 0))
      ? initialBootData
      : (fetchedAssets || []);
    return Array.isArray(data) ? data : [];
  })();

  // Check if current week, future week, or past week editing is allowed
  const currentWeekKey = getCurrentWeekKey();
  const currentWeekDate = getWeekStartDateFromKey(currentWeekKey);
  const periodDate = getWeekStartDateFromKey(periodKey);
  const isCurrentWeek = periodKey === currentWeekKey;
  const isFutureWeek = currentWeekDate && periodDate && periodDate.getTime() > currentWeekDate.getTime();
  const isPastWeek = currentWeekDate && periodDate && periodDate.getTime() < currentWeekDate.getTime();
  
  const canEdit = canEditSection('liveAssets');
  // Admins can always edit past weeks (for cumulative dashboard, mistake corrections, etc.)
  // Viewers can only edit current/future weeks (or past weeks if explicitly enabled)
  const isEditable = canEdit && (isCurrentWeek || isFutureWeek || (isPastWeek && (isAdmin || ALLOW_PAST_WEEK_EDITS)));
  
  // Debug: Log editability status (remove in production if not needed)
  useEffect(() => {
    if (import.meta.env.DEV) {
      logger.debug('LiveAssets editability', {
        canEdit,
        isCurrentWeek,
        isFutureWeek,
        isPastWeek,
        ALLOW_PAST_WEEK_EDITS,
        isEditable,
        periodKey,
        currentWeekKey
      }, 'LiveAssets');
    }
  }, [canEdit, isCurrentWeek, isFutureWeek, isPastWeek, isEditable, periodKey]);

  // Platform detection
  const detectPlatform = (input: string): { platform: PlatformKey | null; title: string | null } => {
    const lowerInput = input.toLowerCase();
    for (const [, handler] of Object.entries(PLATFORM_EMBED_HANDLERS)) {
      for (const pattern of handler.patterns) {
        if (lowerInput.includes(pattern.toLowerCase())) {
          return { platform: handler.platform, title: handler.title };
        }
      }
    }
    if (lowerInput.includes('data-instgrm-permalink') || lowerInput.includes('instagram-media')) {
      return { platform: 'instagram', title: 'Instagram Post' };
    }
    if (lowerInput.includes('twitter-tweet') || lowerInput.includes('data-url')) {
      return { platform: 'twitter', title: 'Twitter Post' };
    }
    if (lowerInput.includes('tiktok-embed') || lowerInput.includes('data-video-id')) {
      return { platform: 'tiktok', title: 'TikTok Video' };
    }
    if (lowerInput.includes('fb-post') || lowerInput.includes('fb-video') || lowerInput.includes('data-href')) {
      return { platform: 'facebook', title: 'Facebook Post' };
    }
    return { platform: null, title: null };
  };

  // Normalize basic domains to full URLs
  const normalizeBasicUrl = (input: string): string => {
    const trimmed = input.trim();
    
    // If it already has a protocol, return as-is
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    
    // If it looks like a domain (contains a dot and no spaces)
    if (trimmed.includes('.') && !trimmed.includes(' ')) {
      // Add https://www. prefix for basic domains
      if (!trimmed.startsWith('www.')) {
        return `https://www.${trimmed}`;
      } else {
        return `https://${trimmed}`;
      }
    }
    
    // Return as-is for other inputs (embed codes, etc.)
    return trimmed;
  };

  // Process pasted input with validation
  const processInput = async (input: string) => {
    const trimmedInput = input.trim();
    if (!trimmedInput) return null;
    
    // Validate input for security
    const inputValidation = await validateEmbedInput(trimmedInput);
    if (!inputValidation.isValid) {
      throw new Error(`Invalid input: ${inputValidation.errors.join(', ')}`);
    }
    
    // First normalize the URL if it's a basic domain
    const normalizedInput = normalizeBasicUrl(inputValidation.sanitized);
    
    const platformInfo = detectPlatform(normalizedInput);
    if (platformInfo.platform) {
      return {
        platform: platformInfo.platform,
        embedCode: normalizedInput,
        originalUrl: normalizedInput,
        title: platformInfo.title || 'Social Media Post',
        isEmbed: true
      };
    }
    
    // For non-embed URLs, treat as link preview
    return {
      platform: 'link' as PlatformKey,
      embedCode: normalizedInput,
      originalUrl: normalizedInput,
      title: 'Social Media Link',
      isEmbed: false
    };
  };

  // Add/update/delete
  const addAsset = useCallback(async (metadata: { platform: PlatformKey; embedCode: string; originalUrl: string; title: string; isEmbed: boolean; postedAt?: string }) => {
    const newAsset: Asset = {
      id: `asset_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      platform: metadata.platform,
      embedCodes: [metadata.embedCode],
      title: metadata.title,
      postedAt: metadata.postedAt || todayYMD(),
      originalUrl: metadata.originalUrl,
      createdAt: todayYMD(),
      updatedAt: todayYMD(),
      approved: true,
      hidden: false, // New assets are always visible
      linkPreview: metadata.isEmbed ? undefined : undefined
    };
    save([newAsset, ...assets]);
    setNewAssetInput(''); setNewAssetTitle(''); setNewAssetDate(''); setIsAddModalOpen(false);
  }, [assets, save]);

  const updateAsset = useCallback(async (updatedAsset: Asset) => {
    if (!isEditable) return;
    
    // Type guard: ensure updatedAsset has required fields
    if (!updatedAsset || !updatedAsset.id) {
      if (import.meta.env.DEV) {
        logger.warn('Invalid asset update', { asset: updatedAsset }, 'LiveAssets');
      }
      return;
    }
    
    // Check if this is an approval change (global update)
    // Approval change: when approved status changes (including undefined -> false or false -> true)
    const originalAsset = assets.find(a => a.id === updatedAsset.id);
    if (!originalAsset) {
      if (import.meta.env.DEV) {
        logger.warn('Asset not found for update', { assetId: updatedAsset.id }, 'LiveAssets');
      }
      return;
    }
    
    const originalApproved = originalAsset.approved !== false; // undefined/true = approved
    const newApproved = updatedAsset.approved !== false; // undefined/true = approved
    const isApprovalChange = originalApproved !== newApproved;
    
    if (isApprovalChange) {
      // Update approval in future weeks only (preserves past weeks as historical record)
      const { updateAssetApprovalGlobally } = await import('../../supabase/snapshotService');
      updateAssetApprovalGlobally(
        updatedAsset.id,
        updatedAsset.approved !== false,
        periodKey, // Current week key - only update future weeks
        'liveAssets',
        workspaceId || undefined
      ).then((updatedWeeks) => {
        if (updatedWeeks > 0) {
          if (import.meta.env.DEV) {
            logger.debug('Asset approval updated in future weeks', {
              assetId: updatedAsset.id,
              approved: updatedAsset.approved !== false,
              currentWeek: periodKey,
              updatedWeeks
            }, 'LiveAssets');
          }
        }
      }).catch((error) => {
        logger.error('Failed to update asset approval in future weeks', {
          error,
          assetId: updatedAsset.id,
          currentWeek: periodKey
        }, 'LiveAssets');
      });
    }
    
    // Also update in current week (for immediate UI feedback)
    const updatedAssets = assets.map((a) => a.id === updatedAsset.id ? updatedAsset : a);
    save(updatedAssets);
  }, [assets, save, isEditable, workspaceId, periodKey]);

  const deleteAsset = useCallback(async (id: string) => {
    if (!isEditable || !id) return;
    
    // Type guard: ensure asset exists
    const assetToDelete = assets.find(a => a.id === id);
    if (!assetToDelete) {
      if (import.meta.env.DEV) {
        logger.warn('Asset not found for deletion', { assetId: id }, 'LiveAssets');
      }
      return;
    }
    
    // Set hidden: true instead of removing the asset (non-destructive deletion)
    const updatedAssets = assets.map((a) => 
      a.id === id ? { ...a, hidden: true, updatedAt: todayYMD() } : a
    );
    save(updatedAssets);
  }, [assets, save, isEditable]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Validate title
      const titleValidation = validateAssetTitle(newAssetTitle);
      if (!titleValidation.isValid) {
        showWarningToast(
          'Invalid Title',
          titleValidation.errors.join(', ')
        );
        return;
      }
      
      // Validate date if provided
      if (newAssetDate) {
        const dateValidation = validateDate(newAssetDate);
        if (!dateValidation.isValid) {
          showWarningToast(
            'Invalid Date',
            dateValidation.errors.join(', ')
          );
          return;
        }
      }
      
      const metadata = await processInput(newAssetInput);
      if (metadata) {
        addAsset({ 
          ...metadata, 
          title: titleValidation.sanitized || metadata.title, 
          postedAt: newAssetDate || new Date().toISOString().split('T')[0] 
        });
        setNewAssetInput(''); 
        setNewAssetTitle(''); 
        setNewAssetDate(''); 
        setIsAddModalOpen(false);
      } else {
        showErrorToast(
          'Invalid Input',
          'Unable to process the provided URL or embed code. Please check the format and try again.'
        );
      }
    } catch (error) {
      showErrorToast(
        'Error Adding Asset',
        error instanceof Error ? error.message : 'Unknown error occurred'
      );
    }
  }, [newAssetInput, newAssetTitle, newAssetDate, addAsset]);

  // Modal hotkeys
  useEffect(() => {
    if (!isAddModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { 
        e.preventDefault(); 
        // Create a synthetic form event for handleSubmit
        const syntheticEvent = new Event('submit') as unknown as React.FormEvent<HTMLFormElement>;
        handleSubmit(syntheticEvent); 
      }
      else if (e.key === 'Escape') { e.preventDefault(); setIsAddModalOpen(false); setNewAssetInput(''); setNewAssetTitle(''); setNewAssetDate(''); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isAddModalOpen]);

  // Sorting/filtering - Newest first for both carousel and masonry grid
  const sortedAssets = useMemo(() => {
    if (assets.length === 0) return [];
    
    // Sort by date (newest first) - this ensures proper chronological order
    // For masonry grid, JavaScript-based column distribution will handle vertical-first display
    return [...assets].sort((a, b) => {
      // Type guard: ensure dates are valid
      const aDate = parseLocalDate(a.postedAt || a.createdAt || todayYMD()) || new Date(0);
      const bDate = parseLocalDate(b.postedAt || b.createdAt || todayYMD()) || new Date(0);
      return bDate.getTime() - aDate.getTime();
    });
  }, [assets]);

  const visibleAssets = useMemo(() => {
    // Filter out hidden assets (non-destructive deletion - hidden assets are preserved in data)
    const nonHiddenAssets = sortedAssets.filter(a => !a.hidden);
    if (canEdit) return nonHiddenAssets;
    return nonHiddenAssets.filter(a => a.approved !== false);
  }, [sortedAssets, canEdit]);

  // URL extraction helpers for embeds
  const extractUrlFromEmbedCode = useCallback((embedCode: string): string | null => {
    if (!embedCode || typeof embedCode !== 'string') return null;
    
    // Try direct URL first
    try {
      const normalized = normalizeUrl(embedCode);
      new URL(normalized);
      return normalized;
    } catch {
      // Not a direct URL, continue to pattern matching
    }
    
    // Extract from iframe src
    const iframeMatch = embedCode.match(/<iframe[^>]*src=["']([^"']+)["']/i);
    if (iframeMatch?.[1]) {
      try {
        const normalized = normalizeUrl(iframeMatch[1]);
        new URL(normalized);
        return normalized;
      } catch {
        // Invalid URL
      }
    }
    
    // Extract from data attributes
    const dataHrefMatch = embedCode.match(/data-href=["']([^"']+)["']/i);
    if (dataHrefMatch?.[1]) {
      try {
        const normalized = normalizeUrl(dataHrefMatch[1]);
        new URL(normalized);
        return normalized;
      } catch {
        // Invalid URL
      }
    }
    
    const igMatch = embedCode.match(/data-instgrm-permalink=["']([^"']+)["']/i);
    if (igMatch?.[1]) {
      try {
        const normalized = normalizeUrl(igMatch[1]);
        new URL(normalized);
        return normalized;
      } catch {
        // Invalid URL
      }
    }
    
    // Extract from platform-specific patterns
    const tweetMatch = embedCode.match(/https:\/\/twitter\.com\/[^\/]+\/status\/\d+/i);
    if (tweetMatch?.[0]) {
      try {
        const normalized = normalizeUrl(tweetMatch[0]);
        new URL(normalized);
        return normalized;
      } catch {
        // Invalid URL
      }
    }
    
    const ttMatch = embedCode.match(/https:\/\/www\.tiktok\.com\/@[^\/]+\/video\/\d+/i);
    if (ttMatch?.[0]) {
      try {
        const normalized = normalizeUrl(ttMatch[0]);
        new URL(normalized);
        return normalized;
      } catch {
        // Invalid URL
      }
    }
    
    const ttIframe = embedCode.match(/<iframe[^>]*src=["']https:\/\/www\.tiktok\.com\/embed\/v2\/(\d+)["']/i);
    if (ttIframe?.[1]) {
      try {
        const url = `https://www.tiktok.com/@user/video/${ttIframe[1]}`;
        const normalized = normalizeUrl(url);
        new URL(normalized);
        return normalized;
      } catch {
        // Invalid URL
      }
    }
    
    return null;
  }, []);

  const getOriginalUrl = useCallback((asset: Asset): string | null => {
    // Type guard: ensure asset is valid
    if (!asset) return null;
    
    // Try originalUrl first
    if (asset.originalUrl && typeof asset.originalUrl === 'string') {
      try {
        const normalized = normalizeUrl(asset.originalUrl);
        new URL(normalized);
        return normalized;
      } catch {
        // Invalid URL
      }
    }
    
    // Fall back to extracting from embed codes
    const firstEmbed = Array.isArray(asset.embedCodes) && asset.embedCodes.length > 0 
      ? asset.embedCodes[0] 
      : '';
    if (firstEmbed) {
      const extracted = extractUrlFromEmbedCode(firstEmbed);
      if (extracted) return extracted;
    }
    
    return null;
  }, [extractUrlFromEmbedCode]);

  const mapToPlatformKey = useCallback((platform: string | null): PlatformKey | null => {
    if (!platform) return null;
    const lower = platform.toLowerCase();
    if (Object.keys(PLATFORM_CONFIGS).includes(lower)) return lower as PlatformKey;
    const map: Record<string, PlatformKey> = {
      'youtube-shorts': 'youtube-shorts', 'instagram-reel': 'instagram', 'fb': 'facebook', 'fb-post': 'facebook', 'fb-video': 'facebook', 'x': 'twitter', 'linkedin-post': 'linkedin'
    };
    return map[lower] || null;
  }, []);

  const renderEmbedMemo = useCallback((asset: Asset, isActive: boolean) => {
    // Type guard: ensure asset is valid
    if (!asset || !asset.id) {
      return (
        <div className="embed-placeholder">
          <div className="embed-placeholder-content">
            <p className="embed-placeholder-text">Invalid asset</p>
          </div>
        </div>
      );
    }
    
    const platformKey = mapToPlatformKey(asset.platform || null);
    const firstEmbed = (Array.isArray(asset.embedCodes) && asset.embedCodes.length > 0)
      ? asset.embedCodes[0]
      : '';
    const url = getOriginalUrl(asset);
    
    if (!platformKey) {
      return (
        <div className="embed-placeholder">
          <div className="embed-placeholder-content">
            <p className="embed-placeholder-text">Unsupported platform: {asset.platform || 'unknown'}</p>
          </div>
        </div>
      );
    }
    
    return (
      <EmbedWithPlaceholder
        platform={platformKey}
        input={platformKey === "twitter" ? firstEmbed || url || "" : url || firstEmbed || ""}
        url={url}
        className="w-full h-full"
        deferLoad={!isActive}
        aspect={1.777}
      />
    );
  }, [mapToPlatformKey, getOriginalUrl]);

  const memoizedGetTitle = useCallback((asset: Asset) => asset.title, []);
  const memoizedGetDate = useCallback((asset: Asset) => asset.postedAt, []);
  const memoizedGetId = useCallback((asset: Asset) => asset.id, []);
  const memoizedGetApproved = useCallback((asset: Asset) => asset.approved !== false, []);
  const memoizedGetPlatform = useCallback((asset: Asset) => asset.platform, []);

  if (typeof fetchedAssets === 'undefined' && !initialBootData && import.meta.env.DEV) {
    return (
      <SectionContainer 
        data-key={sectionRootKey} 
        data-section="live-assets" 
        variant="bare"
        testId="section-live-assets"
        aria-label="Live Assets section"
      >
        <SectionHeader title="Live Assets" href="https://docs.google.com/live-assets-master" />
        <div className="mt-6">
          <AssetsSkeleton />
        </div>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer data-key={sectionRootKey} data-section="live-assets" variant="bare">
      <SectionHeader title="Live Assets" href="https://docs.google.com/live-assets-master">
        <div className="flex items-center gap-3">
          {/* Layout toggle — visible to all users */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleLayoutChange('carousel')}
              className={`updates-toggle ${layoutMode === 'carousel' ? 'updates-toggle--active' : ''}`}
              title="3D Carousel View"
            >
              3D
            </button>
            <button
              onClick={() => handleLayoutChange('masonry')}
              className={`updates-toggle ${layoutMode === 'masonry' ? 'updates-toggle--active' : ''}`}
              title="Masonry Grid View"
            >
              Grid
            </button>
          </div>

          {/* Add button only for editors (respects past week protection) */}
          {isEditable && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accentStrong transition-colors"
            >
              <PlusCircle size={16} />
              Add Asset
            </button>
          )}
        </div>
      </SectionHeader>

      {/* Layouts */}
      <div className="mt-6">
        {visibleAssets.length === 0 ? (
          <div className="text-center py-12 text-textMuted">
            <p className="text-lg mb-2">No assets yet</p>
            <p className="text-sm">
              {isEditable 
                ? 'Add your first social media content to get started' 
                : 'No assets have been added for this week'}
            </p>
          </div>
        ) : layoutMode === 'carousel' ? (
          <Carousel3D
            items={visibleAssets}
            renderEmbed={renderEmbedMemo}
            onUpdate={updateAsset}
            onDelete={deleteAsset}
            canEdit={canEdit}
            getTitle={memoizedGetTitle}
            getDate={memoizedGetDate}
            getUrl={getOriginalUrl}
            getId={memoizedGetId}
            getApproved={memoizedGetApproved}
            getPlatform={memoizedGetPlatform}
          />
        ) : (
          // VERTICAL-FIRST MASONRY: JavaScript-based column distribution for Pinterest-style behavior
          <div className="masonry-grid-vertical" data-layout="masonry">
            {(() => {
              // Distribute assets into columns for vertical-first display
              const numberOfColumns = Math.max(2, Math.min(5, Math.floor((typeof window !== 'undefined' ? window.innerWidth : 1200) / 350)));
              const columns: { items: Asset[] }[] = Array.from({ length: numberOfColumns }, () => ({ items: [] }));
              
              // Distribute assets column-first: newest items go to column 0, then 1, then 2, etc.
              // This ensures when rendered, the newest appear at the top of each column
              visibleAssets.forEach((asset, index) => {
                const columnIndex = index % numberOfColumns;
                columns[columnIndex].items.push(asset);
              });
              
              return (
                <div className="masonry-columns-container">
                  {columns.map((column, columnIndex) => (
                    <div key={columnIndex} className="masonry-column">
                      {column.items.map((asset, assetIndex) => (
                        <ProgressiveAssetLoader
                          key={asset.id}
                          asset={asset}
                          priority={columnIndex === 0 && assetIndex < 2}
                          delay={assetIndex * 50}
                          renderEmbed={(asset, isLoaded) => (
                            <AssetCard
                              asset={asset}
                              onUpdate={updateAsset}
                              onDelete={deleteAsset}
                              isInternalUser={canEdit}
                              deferLoad={!isLoaded}
                            />
                          )}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Add Asset Modal */}
      <Modal isOpen={isAddModalOpen} onClose={() => {
        setIsAddModalOpen(false);
        setNewAssetInput('');
        setNewAssetTitle('');
        setNewAssetDate('');
      }} title="Add New Asset">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="asset-input" className="block text-sm font-medium text-textPrimary mb-2">URL or Embed Code</label>
            <textarea
              id="asset-input"
              value={newAssetInput}
              onChange={(e) => setNewAssetInput(e.target.value)}
              placeholder="Paste a social media URL or embed code..."
              className="w-full h-32 px-4 py-3 input-surface text-textPrimary placeholder:text-textMuted border border-border rounded-lg focus:ring-2 focus:ring-[var(--focus-ring)] focus:border-[var(--focus-ring)] resize-none"
              required
            />
            <p className="mt-2 text-sm text-textMuted">Supports: YouTube, Instagram, TikTok, Twitter, Facebook, LinkedIn, and other platforms</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="asset-title" className="block text-sm font-medium text-textPrimary mb-2">Title</label>
              <input
                id="asset-title"
                type="text"
                value={newAssetTitle}
                onChange={(e) => setNewAssetTitle(e.target.value)}
                placeholder="Enter asset title..."
                className="w-full px-4 py-3 input-surface text-textPrimary placeholder:text-textMuted border border-border rounded-lg focus:ring-2 focus:ring-[var(--focus-ring)] focus:border-[var(--focus-ring)]"
              />
            </div>
            <div>
              <label htmlFor="asset-date" className="block text-sm font-medium text-textPrimary mb-2">Date Posted</label>
              <input
                id="asset-date"
                type="date"
                value={newAssetDate}
                onChange={(e) => setNewAssetDate(e.target.value)}
                className="w-full px-4 py-3 input-surface text-textPrimary border border-border rounded-lg focus:ring-2 focus:ring-[var(--focus-ring)] focus:border-[var(--focus-ring)]"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => {
              setIsAddModalOpen(false);
              setNewAssetInput('');
              setNewAssetTitle('');
              setNewAssetDate('');
            }} className="px-4 py-2 text-textPrimary bg-card border border-border rounded-lg hover:bg-tableHover transition-colors">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accentStrong transition-colors">Add Asset</button>
          </div>
        </form>
      </Modal>
    </SectionContainer>
  );
};

export default LiveAssets;